import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  /**
   * Custom fallback UI. When provided, it replaces the default full-page
   * error screen — used for route-scoped boundaries that should keep the
   * surrounding shell (e.g. the admin nav) visible. Receives the caught
   * error and a `reset` callback that clears the error state.
   */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /**
   * When any value in this array changes, a boundary that is currently
   * showing an error resets itself. Pass the current route path so navigating
   * to a different page automatically clears a previously-crashed one.
   */
  resetKeys?: ReadonlyArray<unknown>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App error:', error, info);
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;
    const prev = prevProps.resetKeys;
    const next = this.props.resetKeys;
    if (!prev || !next) return;
    const changed = prev.length !== next.length || next.some((value, i) => !Object.is(value, prev[i]));
    if (changed) this.reset();
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      // App-root fallback (outside the router). It must never be a dead end:
      // "Try again" recovers a transient crash, and "Reload app" navigates home
      // with fresh state to escape a deterministic one. Theme-aware tokens only;
      // the raw error stays in the console (componentDidCatch) rather than being
      // shown to clinical users.
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
          <div className="w-full max-w-lg rounded-2xl border border-border bg-surface-raised p-8 text-center shadow-sm">
            <span className="mx-auto flex size-12 items-center justify-center rounded bg-status-attention-bg text-status-attention-text">
              <AlertTriangle className="size-6" />
            </span>
            <h1 className="mt-4 text-xl font-medium">Something went wrong</h1>
            <p className="mt-3 text-sm leading-6 text-text-tertiary">
              The app hit an unexpected error while loading this screen. Reloading usually
              fixes it. If it keeps happening, contact your March Health administrator.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => { window.location.href = '/'; }}
                className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
              >
                <RefreshCw className="size-4" />
                Reload app
              </button>
              <button
                type="button"
                onClick={this.reset}
                className="inline-flex h-9 w-full items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-surface-hover sm:w-auto"
              >
                Try again
              </button>
            </div>
            <p className="mt-4 text-xs text-text-tertiary/70">
              Technical details are in the browser console.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
